import {Vec2} from "./vec2";

export class Mat3 {
  data: Float32Array = new Float32Array(9);

  private constructor() {}

  static zero(): Mat3 {
    return new Mat3();
  }

  static identity(): Mat3 {
    const m = Mat3.zero();

    m.data[0] = 1;
    m.data[4] = 1;
    m.data[8] = 1;

    return m;
  }

  static fromTranslation(translation: Vec2): Mat3 {
    const m = Mat3.identity();
    m.data[6] = translation.x;
    m.data[7] = translation.y;

    return m;
  }

  static fromScale(scale: Vec2): Mat3 {
    const sx = scale.x;
    const sy = scale.y;

    const m = Mat3.identity();
    m.data[0] = sx;
    m.data[4] = sy;

    return m;
  }

  static fromScaleTranslation(scale: Vec2, translation: Vec2): Mat3 {
    const sx = scale.x;
    const sy = scale.y;

    const m = Mat3.identity();
    m.data[0] = sx;
    m.data[4] = sy;
    m.data[6] = translation.x;
    m.data[7] = translation.y;

    return m;
  }

  equals(other: Mat3): boolean {
    for (let i = 0; i < 9; i++) {
      if (this.data[i] !== other.data[i]) return false;
    }
    return true;
  }

  transformPoint(vector: Vec2): Vec2 {
    const x = vector.x * this.data[0] + vector.y * this.data[3] + this.data[6];
    const y = vector.x * this.data[1] + vector.y * this.data[4] + this.data[7];
    return new Vec2(x, y);
  }

  mul(other: Mat3): Mat3 {
    const res = new Mat3();

    for (let row = 0; row < 3; row++) {
      for (let col = 0; col < 3; col++) {
        for (let k = 0; k < 3; k++) {
          res.data[row + col * 3] += this.data[row + k * 3] * other.data[k + col * 3];
        }
      }
    }

    return res;
  }

  det(): number {
    return (
      this.data[0] * (this.data[4] * this.data[8] - this.data[5] * this.data[7]) -
      this.data[1] * (this.data[3] * this.data[8] - this.data[5] * this.data[6]) +
      this.data[2] * (this.data[3] * this.data[7] - this.data[4] * this.data[6])
    );
  }

  inverse(): Mat3 {
    const det = this.det();
    if (det === 0) throw new Error("Matrix cannot be inverted: determinant is zero.");

    const invDet = 1 / det;
    const m = new Mat3();

    m.data[0] = (this.data[4] * this.data[8] - this.data[5] * this.data[7]) * invDet;
    m.data[1] = (this.data[2] * this.data[7] - this.data[1] * this.data[8]) * invDet;
    m.data[2] = (this.data[1] * this.data[5] - this.data[2] * this.data[4]) * invDet;
    m.data[3] = (this.data[5] * this.data[6] - this.data[3] * this.data[8]) * invDet;
    m.data[4] = (this.data[0] * this.data[8] - this.data[2] * this.data[6]) * invDet;
    m.data[5] = (this.data[2] * this.data[3] - this.data[0] * this.data[5]) * invDet;
    m.data[6] = (this.data[3] * this.data[7] - this.data[4] * this.data[6]) * invDet;
    m.data[7] = (this.data[1] * this.data[6] - this.data[0] * this.data[7]) * invDet;
    m.data[8] = (this.data[0] * this.data[4] - this.data[1] * this.data[3]) * invDet;

    return m;
  }

  /** Extract the translational part of this transformation matrix. */
  translation(): Vec2 {
    return new Vec2(this.data[6], this.data[7]);
  }

  /** Extract the scaling part of this transformation matrix. */
  scale(): Vec2 {
    return new Vec2(
      Math.sqrt(this.data[0] * this.data[0] + this.data[1] * this.data[1]),
      Math.sqrt(this.data[3] * this.data[3] + this.data[4] * this.data[4]),
    );
  }
}
